import {
	failureActionHandler,
	requestActionHandler,
	successActionHandler,
} from 'store';
import injectReducer from 'store/injectReducer';
import { IPagination } from 'models';
import { IClientList } from 'modules/admin-panel/pages/clients/models';
import {
	IPartnersReducer,
	IAdmin,
	IPartnerData,
	IPartnerList,
} from 'modules/admin-panel/pages/partners/models';
import * as types from 'modules/admin-panel/pages/partners/ActionTypes';

export const InitialPartnersReducer: IPartnersReducer = {
	isLoading: false,
	isContentLoading: false,
	isClientListLoading: false,
	isAdminListLoading: false,
	isNewAdminCreated: false,
	isNewUserCreating: false,
	partnerList: [],
	pagination: {},
};

export default injectReducer(InitialPartnersReducer, {
	[types.GET_PARTNER_LIST.REQUEST]: requestActionHandler,
	[types.UPDATE_PARTNER_LIST.REQUEST]: requestActionHandler,

	[types.POST_NEW_PARTNER.REQUEST]: requestActionHandler,
	[types.DELETE_PARTNER.REQUEST]: requestActionHandler,
	[types.POST_NEW_USER.REQUEST]: (state: IPartnersReducer) => ({
		...state,
		isNewUserCreating: true,
	}),
	[types.GET_PARTNER_DATA.REQUEST]: requestActionHandler,
	[types.UPDATE_PARTNER_DATA.REQUEST]: requestActionHandler,

	[types.GET_PARTNER_ADMINS.REQUEST]: (state: IPartnersReducer) => ({
		...state,
		isAdminListLoading: true,
	}),

	[types.GET_PARTNER_DETAILS.REQUEST]: (state: IPartnersReducer) => ({
		...state,
		isContentLoading: true,
	}),
	[types.GET_PARTNER_CLIENTS.REQUEST]: (state: IPartnersReducer) => ({
		...state,
		isClientListLoading: true,
	}),

	[types.GET_PARTNER_LIST.FAILURE]: failureActionHandler,
	[types.UPDATE_PARTNER_LIST.FAILURE]: failureActionHandler,
	[types.DELETE_PARTNER.FAILURE]: failureActionHandler,

	[types.POST_NEW_PARTNER.FAILURE]: failureActionHandler,
	[types.POST_NEW_USER.FAILURE]: (
		state: IPartnersReducer,
		{ payload }: { payload: Error },
	) => ({
		...state,
		isNewUserCreating: false,
		newUserError: payload,
	}),
	[types.GET_PARTNER_DATA.FAILURE]: failureActionHandler,
	[types.UPDATE_PARTNER_DATA.FAILURE]: failureActionHandler,

	[types.GET_PARTNER_ADMINS.FAILURE]: (
		state: IPartnersReducer,
		{ payload }: { payload: Error },
	) => ({
		...failureActionHandler(state, { payload }),
		isAdminListLoading: false,
	}),

	[types.GET_PARTNER_DETAILS.FAILURE]: (
		state: IPartnersReducer,
		{ payload }: { payload: Error },
	) => ({
		...state,
		error: payload,
		isContentLoading: false,
	}),
	[types.GET_PARTNER_CLIENTS.FAILURE]: (
		state: IPartnersReducer,
		{ payload }: { payload: Error },
	) => ({
		...state,
		error: payload,
		isClientListLoading: false,
	}),

	[types.POST_NEW_PARTNER.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: { id: string } },
	) => ({
		...successActionHandler(state),
		newPartnerId: payload.id,
	}),

	[types.DELETE_PARTNER.SUCCESS]: (state: IPartnersReducer) => ({
		...successActionHandler(state),
		isPartnerDeleted: true,
	}),

	[types.GET_PARTNER_LIST.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: IPagination<IPartnerList> },
	): IPartnersReducer => ({
		...successActionHandler(state),
		partnerList: payload.content || [],
		pagination: {
			last: payload.last,
		},
	}),

	[types.UPDATE_PARTNER_LIST.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: IPagination<IPartnerList> },
	): IPartnersReducer => ({
		...successActionHandler(state),
		partnerList: [...state.partnerList, ...(payload.content || [])],
		pagination: {
			last: payload.last,
		},
	}),

	[types.GET_PARTNER_DETAILS.SUCCESS]: (state: IPartnersReducer) => ({
		...successActionHandler(state),
		isContentLoading: false,
	}),

	[types.GET_PARTNER_DATA.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: IPartnerData },
	): IPartnersReducer => ({
		...successActionHandler(state),
		partnerData: payload,
	}),

	[types.UPDATE_PARTNER_DATA.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: string },
	): IPartnersReducer => ({
		...successActionHandler(state),
		isPartnerUpdated: payload,
	}),

	[types.GET_PARTNER_ADMINS.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: IAdmin[] },
	): IPartnersReducer => ({
		...successActionHandler(state),
		adminList: payload,
		isAdminListLoading: false,
	}),

	[types.GET_PARTNER_CLIENTS.SUCCESS]: (
		state: IPartnersReducer,
		{ payload }: { payload: IPagination<IClientList> },
	): IPartnersReducer => ({
		...state,
		clientList: payload,
		isClientListLoading: false,
		error: undefined,
	}),

	[types.RESET_PARTNER_LIST]: (state: IPartnersReducer): IPartnersReducer => ({
		...successActionHandler(state),
		partnerList: [],
	}),

	[types.RESET_NEW_PARTNER]: (state: IPartnersReducer): IPartnersReducer => ({
		...successActionHandler(state),
		newPartnerId: undefined,
	}),

	[types.RESET_PARTNER_DETAILS]: (
		state: IPartnersReducer,
	): IPartnersReducer => ({
		...successActionHandler(state),
		partner: undefined,
	}),

	[types.RESET_PARTNER_DATA]: (state: IPartnersReducer): IPartnersReducer => ({
		...successActionHandler(state),
		partnerData: undefined,
	}),

	[types.RESET_UPDATE_PARTNER_DATA]: (
		state: IPartnersReducer,
	): IPartnersReducer => ({
		...successActionHandler(state),
		isPartnerUpdated: undefined,
	}),

	[types.POST_NEW_USER.SUCCESS]: (
		state: IPartnersReducer,
	): IPartnersReducer => ({
		...successActionHandler(state),
		isNewUserCreating: false,
		isNewAdminCreated: true,
	}),

	[types.RESET_IS_PARTNER_DELETED]: (
		state: IPartnersReducer,
	): IPartnersReducer => ({
		...successActionHandler(state),
		isPartnerDeleted: undefined,
	}),

	[types.RESET_USER_FORM_DATA]: (
		state: IPartnersReducer,
	): IPartnersReducer => ({
		...state,
		isNewAdminCreated: false,
		newUserError: undefined,
	}),

	[types.RESET_ERROR]: (state: IPartnersReducer): IPartnersReducer => ({
		...state,
		error: undefined,
	}),
});
